import { FC } from "react";
import {
  Box,
  Stack,
  Typography,
  LinearProgress,
  Alert,
  CircularProgress,
  Button,
} from "@mui/material";
import {
  CheckCircle,
  CloudDownload,
  Error as ErrorIcon,
} from "@mui/icons-material";

import { tokens } from "@/tokens";

const pulseKeyframes = {
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1 },
    "50%": { opacity: 0.4 },
  },
};

const cardSx = {
  border: `1px solid ${tokens.border.default}`,
  borderRadius: tokens.radius.lg,
  overflow: "hidden",
};
const cardBodySx = { p: 4 };

const formatAdjustmentType = (type?: string): string => {
  switch (type) {
    case "value_percentage":
      return "Value Adjustment";
    case "duty_rate_adjustment":
      return "Duty Rate Adjustment";
    case "combined_value_and_duty":
      return "Combined Value & Duty Adjustment";
    case "line_specific":
      return "Line-Specific Adjustment";
    default:
      return "Adjustment";
  }
};

interface StepProgressProps {
  status: "PENDING" | "SUCCESS" | "FAILED";
  progress: number;
  downloadUrl?: string;
  error?: string;
  fileId?: string;
  adjustmentType?: string;
  onDownload: (url: string) => void;
  onClose: () => void;
}

const StepProgress: FC<StepProgressProps> = ({
  status,
  progress,
  downloadUrl,
  error,
  fileId,
  adjustmentType,
  onDownload,
  onClose,
}) => {
  const formattedAdjustmentType = formatAdjustmentType(adjustmentType);

  const getStatusIcon = () => {
    switch (status) {
      case "SUCCESS":
        return (
          <CheckCircle sx={{ fontSize: 48, color: tokens.status.success }} />
        );
      case "FAILED":
        return <ErrorIcon sx={{ fontSize: 48, color: tokens.status.danger }} />;
      default:
        return <CircularProgress size={48} />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case "SUCCESS":
        return "Processing Complete";
      case "FAILED":
        return "Processing Failed";
      default:
        return "Processing Adjustment...";
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case "SUCCESS":
        return "Your adjustment has been processed successfully and is ready for download.";
      case "FAILED":
        return (
          error ||
          "An error occurred while processing the adjustment. Please try again."
        );
      default:
        return "Please wait while we process your adjustment data. This may take several minutes for large datasets.";
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      onDownload(downloadUrl);
    }
  };

  return (
    <Box sx={{ ...cardSx, maxWidth: 600, mx: "auto" }}>
      <Box sx={cardBodySx}>
        <Stack spacing={4} alignItems="center">
          {/* Status Icon */}
          <Box>{getStatusIcon()}</Box>

          {/* Status Text */}
          <Stack spacing={1} alignItems="center" textAlign="center">
            <Typography
              variant="h6"
              sx={{
                color:
                  status === "FAILED"
                    ? tokens.status.danger
                    : tokens.text.primary,
              }}
            >
              {getStatusText()}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: tokens.text.secondary }}
              maxWidth="400px"
            >
              {getStatusMessage()}
            </Typography>
          </Stack>

          {/* Progress Bar */}
          {status === "PENDING" && (
            <Box width="100%" maxWidth="400px">
              <Stack spacing={2}>
                <LinearProgress
                  variant={progress > 0 ? "determinate" : "indeterminate"}
                  value={progress}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                {progress > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ color: tokens.text.secondary }}
                    textAlign="center"
                  >
                    {Math.round(progress)}% Complete
                  </Typography>
                )}
              </Stack>
            </Box>
          )}

          {/* File ID for tracking */}
          {fileId && status === "PENDING" && (
            <Box>
              <Typography
                variant="caption"
                sx={{ color: tokens.text.secondary }}
                fontFamily="monospace"
              >
                Processing ID: {fileId}
              </Typography>
            </Box>
          )}

          {/* Success Alert */}
          {status === "SUCCESS" && (
            <Alert severity="success" sx={{ width: "100%" }}>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Success!</strong> Your {formattedAdjustmentType}{" "}
                  processing is complete.
                </Typography>
                {downloadUrl && (
                  <Typography variant="body2">
                    Click "Download Results" to get your processed data file.
                  </Typography>
                )}
              </Stack>
            </Alert>
          )}

          {/* Error Alert */}
          {status === "FAILED" && (
            <Alert severity="error" sx={{ width: "100%" }}>
              <Stack spacing={1}>
                <Typography variant="body2">
                  <strong>Processing Failed</strong>
                </Typography>
                {error && (
                  <Typography variant="body2">{error}</Typography>
                )}
                <Typography variant="body2">
                  Please check your configuration and try again. If the problem
                  persists, contact support.
                </Typography>
              </Stack>
            </Alert>
          )}

          {/* Download Button */}
          {status === "SUCCESS" && downloadUrl && (
            <Box
              onClick={handleDownload}
              sx={{
                cursor: "pointer",
                p: 2,
                borderRadius: 2,
                border: "2px dashed",
                borderColor: tokens.status.success,
                bgcolor: tokens.status.successBg,
                display: "flex",
                alignItems: "center",
                gap: 1,
                "&:hover": {
                  bgcolor: tokens.status.success,
                  color: "white",
                },
                transition: "all 0.2s",
              }}
            >
              <CloudDownload />
              <Typography variant="body2" fontWeight="medium">
                Click to Download Results
              </Typography>
            </Box>
          )}

          {/* Processing Steps Checklist */}
          {status === "PENDING" && (
            <Box sx={{ width: "100%", textAlign: "center", ...pulseKeyframes }}>
              <Typography
                variant="body2"
                sx={{ color: tokens.text.secondary }}
              >
                Processing steps:
              </Typography>
              <Stack spacing={0.5} mt={1}>
                {[
                  { label: "Configuration validated", done: true },
                  { label: "Data scope analyzed", done: true },
                  { label: "Applying adjustments", done: progress > 30 },
                  { label: "Generating report", done: progress > 80 },
                  { label: "Finalizing results", done: progress >= 100 },
                ].map((step) => (
                  <Stack key={step.label} direction="row" alignItems="center" justifyContent="center" spacing={0.75}>
                    {step.done ? (
                      <CheckCircle sx={{ fontSize: 14, color: tokens.status.success }} />
                    ) : (
                      <CircularProgress size={14} sx={{ animation: "pulse 1.5s ease-in-out infinite" }} />
                    )}
                    <Typography
                      variant="caption"
                      sx={{
                        color: step.done ? tokens.text.primary : tokens.text.secondary,
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {/* Action Buttons */}
          {status === "FAILED" && (
            <Button variant="outlined" onClick={onClose}>
              Close
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default StepProgress;
